// Pantalla al entrar en el ERP (ruta "/" y tras el login) para quien no es
// dirección: Mi Inicio hasta que ficha y, una vez fichado, el calendario.
// Cada empleado puede tener en su ficha "Siempre el calendario" o "Siempre Mi
// Inicio" (pantalla_inicio). El ajuste y el fichaje de hoy se piden al servidor
// en el momento (GET /acceso/mi-pantalla-inicio), así un cambio en la ficha
// llega sin volver a iniciar sesión; si falla, se usa lo guardado en el login y
// las sesiones antiguas sin el campo se tratan como automática.
// Solo decide al entrar: los enlaces del menú (Mi Inicio, Calendario) no pasan por aquí.
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado } from '../context/VistaEmpleadoContext';
import { accesoAPI } from '../api/api';
import { registrarAccion } from '../utils/usoErp';

export const RUTA_MI_INICIO = '/dashboard-empleado';
export const RUTA_CALENDARIO = '/calendario-eventos';

// Valores del ajuste de la ficha; cualquier otro (o vacío) es automática
export const PANTALLAS_INICIO = ['auto', 'calendario', 'mi_inicio'];

export const normalizarPantalla = (valor) => (PANTALLAS_INICIO.includes(valor) ? valor : 'auto');

// Destino según el ajuste, el fichaje de hoy y si puede ver el calendario
export const destinoInicial = ({
  pantalla, haFichado, puedeCalendario, tourPendiente, tourPospuesto = false,
}) => {
  if (!puedeCalendario) return RUTA_MI_INICIO;
  // El tour de bienvenida se abre solo en Mi Inicio: mientras esté pendiente se
  // entra ahí, salvo que lo haya saltado (pospuesto: ya no se abre solo y Mi
  // Inicio enseña un aviso para verlo; manda el ajuste de siempre)
  if (tourPendiente && !tourPospuesto) return RUTA_MI_INICIO;
  const ajuste = normalizarPantalla(pantalla);
  if (ajuste === 'calendario') return RUTA_CALENDARIO;
  if (ajuste === 'mi_inicio') return RUTA_MI_INICIO;
  return haFichado ? RUTA_CALENDARIO : RUTA_MI_INICIO;
};

const Cargando = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="text-gray-500">Cargando...</div>
  </div>
);

const InicioSegunFichaje = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const { usuarioEfectivo, simulando, puedeVerRuta } = useVistaEmpleado();
  const [destino, setDestino] = useState(null);
  const registrado = useRef(false);

  const usuario = usuarioEfectivo || user;
  const puedeCalendario = puedeVerRuta ? puedeVerRuta(RUTA_CALENDARIO, usuario) : true;
  // Lo guardado en el login (las sesiones anteriores a este cambio no lo traen: automática)
  const pantallaGuardada = normalizarPantalla(user?.pantalla_inicio);
  const activo = isAuthenticated && !loading && !simulando;

  useEffect(() => {
    if (!activo) return undefined;
    if (!puedeCalendario) {
      setDestino(RUTA_MI_INICIO);
      return undefined;
    }
    let vivo = true;
    accesoAPI.miPantallaInicio()
      .then((res) => {
        if (!vivo) return;
        const d = res?.data || {};
        setDestino(destinoInicial({
          pantalla: d.pantalla_inicio,
          haFichado: d.ha_fichado_hoy === true,
          tourPendiente: d.tour_pendiente === true,
          tourPospuesto: d.tour_pospuesto === true,
          puedeCalendario,
        }));
      })
      .catch(() => {
        // Sin respuesta: "Siempre el calendario" se respeta con lo guardado; si no, Mi Inicio
        if (vivo) setDestino(pantallaGuardada === 'calendario' ? RUTA_CALENDARIO : RUTA_MI_INICIO);
      });
    return () => { vivo = false; };
  }, [activo, puedeCalendario, pantallaGuardada]);

  useEffect(() => {
    if (!destino || registrado.current) return;
    registrado.current = true;
    registrarAccion('inicio.redirigido', destino);
  }, [destino]);

  if (loading) return <Cargando />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // En la vista de simulación no se redirige: se queda en Mi Inicio del simulado
  if (simulando) return <Navigate to={RUTA_MI_INICIO} replace />;
  if (!destino) return <Cargando />;
  return <Navigate to={destino} replace />;
};

export default InicioSegunFichaje;
